import MainLayout from "@/components/layout/MainLayout";
import Screenshot from "@/components/common/Screenshot";

const UserGuide = () => {
  return (
    <MainLayout>
      <div className="container-page max-w-4xl">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-6">
            Как преобразовать аудио и видео в текст: Полное руководство по Voice2Doc
          </h1>

          <p className="text-base sm:text-lg text-foreground mb-8">
            Здравствуйте! Добро пожаловать в Voice2Doc. Это полное руководство поможет вам понять, как выполнять{" "}
            <strong>транскрибацию аудиозаписей</strong>, делать <strong>расшифровку видео</strong> и автоматически
            создавать структурированные документы: протоколы совещаний, конспекты лекций, саммари встреч и многое
            другое.
          </p>

          <hr className="my-8" />

          <h4 className="text-lg sm:text-xl font-semibold mb-4">Содержание (Быстрая навигация)</h4>
          <ol className="list-decimal list-inside space-y-2 mb-8">
            <li>
              <a href="#start" className="text-blue-600 hover:text-blue-800">
                Раздел 1: Расшифровка аудио в текст
              </a>
            </li>
            <li>
              <a href="#templates" className="text-blue-600 hover:text-blue-800">
                Раздел 2: Как создавать шаблоны для точной транскрибации
              </a>
            </li>
            <li>
              <a href="#balance" className="text-blue-600 hover:text-blue-800">
                Раздел 3: Тарифы и управление балансом
              </a>
            </li>
            <li>
              <a href="#faq" className="text-blue-600 hover:text-blue-800">
                Раздел 4: Часто задаваемые вопросы о расшифровке аудио
              </a>
            </li>
          </ol>

          <hr className="my-8" />

          <div id="start" className="scroll-mt-20">
            <h2 className="text-xl sm:text-2xl font-bold mb-6">Раздел 1: Расшифровка аудио в текст</h2>

            <p className="mb-6">Прежде чем мы начнем, давайте кратко разберем, как устроен процесс:</p>
            <ol className="list-decimal list-inside space-y-2 mb-6">
              <li>
                Сначала наш сервис <strong>превращает всю речь из вашей записи в сплошной текст</strong> (это называется
                транскрибация).
              </li>
              <li>
                Затем, с помощью выбранного шаблона, наш ИИ превращает этот текст в аккуратный документ,{" "}
                <strong>например,</strong> протокол, конспект или саммари.
              </li>
            </ol>

            <p className="mb-8">А теперь давайте сделаем это на практике!</p>

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Шаг 1: Загрузите аудио или видео файл</h3>
            <p className="mb-4">
              На главной странице нажмите кнопку <strong>"Выбрать файл"</strong> или просто перетащите ваш файл в
              специальную область.
            </p>

            <Screenshot description="Главный экран с областью загрузки" imageName="screen_01" />

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Шаг 2: Выберите шаблон обработки</h3>
            <p className="mb-4">
              Шаблон — это как рецепт из кулинарной книги для вашего будущего документа. Он указывает ИИ, какую
              структуру придать тексту, чтобы получить протокол совещания, конспект лекции или саммари встречи.
            </p>
            <p className="mb-4">
              Выберите один из предложенных шаблонов, чтобы получить <strong>готовый к использованию документ</strong>.
            </p>

            <Screenshot description="Экран выбора шаблонов" imageName="screen_02" />

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Шаг 3: Запустите транскрибацию</h3>
            <p className="mb-4">
              Нажмите кнопку <strong>"Обработать файл"</strong>. Начнется загрузка и последующая расшифровка записи.
            </p>

            <Screenshot description="Диалоговое окно с прогрессом загрузки файла" imageName="screen_03" />

            <p className="mb-4">
              После загрузки вы автоматически попадете на страницу <strong>"Мои записи"</strong>, где увидите вашу
              запись со статусом <code className="bg-muted px-1 rounded">Обработка</code>. Процесс может занять
              несколько минут.
            </p>

            <Screenshot description="Страница 'Мои записи' со статусом 'Обработка'" imageName="screen_04" />

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Шаг 4: Получите готовый документ</h3>
            <p className="mb-4">
              Когда статус изменится на <code className="bg-muted px-1 rounded">Завершено</code>, нажмите на запись.
            </p>
            <p className="mb-4">
              На странице результата вы увидите две вкладки: <strong>«Обработанный текст»</strong> и «Транскрипция»
              (полная стенограмма с тайм-кодами).
            </p>

            <Screenshot description="Страница результата с готовым протоколом" imageName="screen_05" />

            <Screenshot description="Страница результата с транскрипцией" imageName="screen_06" />

            <p className="mb-6">
              Теперь вы можете <strong>скачать</strong> готовый протокол или <strong>скопировать</strong> его текст.{" "}
              <strong>Важно:</strong> кнопки действуют на ту вкладку, которая у вас открыта.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <p className="text-green-800 mb-3">
                <strong>Что дальше?</strong>
              </p>
              <p className="text-green-700 mb-4">
                Поздравляем! Вы освоили базовую расшифровку аудио. Теперь у вас есть два пути:
              </p>
              <ul className="list-disc list-inside space-y-2 text-green-700">
                <li>
                  Хотите научиться создавать идеальные протоколы и саммари? Переходите в{" "}
                  <strong>
                    <a href="#templates" className="text-green-800 hover:text-green-900">
                      Раздел 2: Как создавать шаблоны для точной транскрибации
                    </a>
                  </strong>
                  .
                </li>
                <li>
                  Остались вопросы? Ответы на них вы найдете в{" "}
                  <strong>
                    <a href="#faq" className="text-green-800 hover:text-green-900">
                      Разделе 4: Часто задаваемые вопросы о расшифровке аудио
                    </a>
                  </strong>
                  .
                </li>
              </ul>
            </div>
          </div>

          <hr className="my-8" />

          <div id="templates" className="scroll-mt-20">
            <h2 className="text-xl sm:text-2xl font-bold mb-6">
              Раздел 2: Как создавать шаблоны для точной транскрибации
            </h2>

            <p className="mb-6">
              Этот раздел — ваше полное руководство по созданию персональных шаблонов для получения идеальных
              протоколов, конспектов и саммари.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Что такое шаблон и промпт для ИИ?</h3>
            <ul className="list-disc list-inside space-y-2 mb-6">
              <li>
                <strong>Шаблон</strong> — это ваш личный <strong>"кулинарный рецепт"</strong> для создания документа.
              </li>
              <li>
                <strong>Промпт</strong> — это текстовая инструкция для ИИ, которая объясняет, как именно нужно
                обработать стенограмму.
              </li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Как создать шаблон для расшифровки</h3>

            <h4 className="text-base sm:text-lg font-semibold mb-3">Шаг 1: Откройте редактор</h4>
            <p className="mb-4">
              Перейдите в раздел <strong>«Шаблоны»</strong> и нажмите <strong>«Создать шаблон»</strong>.
            </p>

            <Screenshot description="Экран создания нового шаблона" imageName="screen_08" />

            <h4 className="text-lg font-semibold mb-3">Шаг 2: Заполните поля шаблона</h4>
            <ol className="list-decimal list-inside space-y-3 mb-6">
              <li>
                <strong>Название шаблона:</strong> Придумайте понятное имя.
              </li>
              <li>
                <strong>Описание (рекомендуется):</strong> Добавьте несколько слов о назначении.
              </li>
              <li>
                <strong>Текст промпта (инструкция для ИИ):</strong>
                <p className="mt-2 mb-3">
                  Создание эффективного промпта требует опыта. Чтобы сделать этот процесс простым, мы создали{" "}
                  <strong>ИИ-Помощника</strong>. Это ваш личный ассистент, который напишет технически грамотный промпт
                  за вас.
                </p>
                <p className="mb-2">
                  <strong>Как им воспользоваться:</strong>
                </p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>
                    Нажмите <strong>"Помощник"</strong>.
                  </li>
                  <li>
                    В окне опишите своими словами, какой документ хотите получить (например, "детальный протокол
                    совещания с задачами и ответственными").
                  </li>
                  <li>
                    Нажмите <strong>"Сгенерировать промпт"</strong>.
                  </li>
                </ol>
              </li>
            </ol>

            <Screenshot description="Модальное окно 'ИИ-помощника'" imageName="screen_09" />

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800">
                <strong>Совет:</strong> После генерации вы всегда можете доработать промпт вручную.
              </p>
            </div>

            <h4 className="text-lg font-semibold mb-3">Шаг 3: Протестируйте шаблон</h4>
            <p className="mb-4">
              Прежде чем сохранять, проверьте его в действии, <strong>не тратя минуты с баланса</strong>:
            </p>
            <ol className="list-decimal list-inside space-y-2 mb-4">
              <li>
                Нажмите <strong>"Тестировать"</strong>.
              </li>
              <li>Выберите одну из ранее сделанных записей.</li>
              <li>
                Через несколько секунд сервис покажет результат работы нового промпта. Если результат вас не устраивает,
                внесите правки в промпт и протестируйте снова.
              </li>
            </ol>

            <Screenshot description="Процесс тестирования шаблона" imageName="screen_10" />

            <h4 className="text-lg font-semibold mb-3">Шаг 4: Сохраните шаблон</h4>
            <p className="mb-6">
              Когда результат вас удовлетворил, нажмите <strong>"Создать шаблон"</strong>.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Полезные приемы</h3>
            <ul className="list-disc list-inside space-y-3 mb-8">
              <li>
                <strong>Как адаптировать системный шаблон?</strong>
                <br />
                Откройте любой системный шаблон и внизу нажмите кнопку <strong>"Создать свой шаблон"</strong>. Сервис
                создаст редактируемую копию.
              </li>
              <li>
                <strong>Как применить новый шаблон к старой записи? (Бесплатно!)</strong>
                <br />
                Откройте любую запись, нажмите <strong>"Обработать заново"</strong> и выберите ваш новый шаблон.
                Повторная обработка <strong>не списывает минуты с баланса</strong>.
              </li>
            </ul>
          </div>

          <hr className="my-8" />

          <div id="balance" className="scroll-mt-20">
            <h2 className="text-xl sm:text-2xl font-bold mb-6">Раздел 3: Тарифы и управление балансом</h2>

            <p className="mb-6">
              Сервис работает по предоплате. Вы покупаете пакеты минут и используете их для транскрибации аудио и видео.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Как пополнить баланс</h3>
            <p className="mb-4">
              В разделе <strong>"Баланс"</strong> выберите подходящий пакет минут и нажмите <strong>"Выбрать"</strong>.{" "}
              <strong>Купленные минуты будут зачислены на ваш баланс сразу после успешного платежа.</strong>
            </p>

            {/* <Screenshot description="Страница 'Баланс' с пакетами минут" imageName="screen_01" /> */}

            <h3 className="text-lg sm:text-xl font-semibold mb-4">Как списываются минуты</h3>
            <ul className="list-disc list-inside space-y-2 mb-6">
              <li>
                Когда вы загружаете <strong>новый файл</strong> и запускаете его первую обработку, минуты{" "}
                <strong>"Замораживаются"</strong> на балансе.
              </li>
              <li>После успешной транскрибации они окончательно списываются.</li>
            </ul>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
              <p className="text-green-800">
                <strong>Напоминаем:</strong> Повторная обработка записи (функция "Обработать заново"){" "}
                <strong>всегда бесплатна</strong>.
              </p>
            </div>
          </div>

          <hr className="my-8" />

          <div id="faq" className="scroll-mt-20">
            <h2 className="text-xl sm:text-2xl font-bold mb-6">
              Раздел 4: Часто задаваемые вопросы о транскрибации аудио
            </h2>

            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold mb-2">В: Какие ограничения у сервиса расшифровки аудио?</h4>
                <p className="text-foreground">О: Максимальный размер файла — 5 ГБ, длительность — 10 часов.</p>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-2">В: Как работает определение спикеров при транскрибации?</h4>
                <p className="text-foreground">
                  О: Наш ИИ автоматически анализирует голоса и нумерует их (
                  <code className="bg-muted px-1 rounded">SPEAKER_00</code>,{" "}
                  <code className="bg-muted px-1 rounded">SPEAKER_01</code>).
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-2">
                  В: Можно ли отредактировать стенограмму после расшифровки?
                </h4>
                <p className="text-foreground">
                  О: На данный момент редактирование исходной стенограммы не предусмотрено.
                </p>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-2">В: Что делать, если в итоговом документе есть ошибки?</h4>
                <p className="text-foreground">
                  О: Попробуйте улучшить промпт в шаблоне и <strong>"Обработать заново"</strong> запись (это бесплатно)
                  или скачайте <code className="bg-muted px-1 rounded">.docx</code> и внесите правки вручную.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-8" />

          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Надеемся, это руководство было для вас полезным. Успешной работы с Voice2Doc!
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default UserGuide;
